import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import { defaultStatusOptions } from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Option } from '@nxsys/types/models/Common';
import { validatePhone } from '@nxsys/utils/validator';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import { Ref, useImperativeHandle } from 'react';

export type AgencyContactFormValues = {
  companyRef: Option<string> | null;
  department: string;
  email: string;
  name: string;
  phone: string;
  status: Option<string> | null;
};

const defaultValues: AgencyContactFormValues = {
  companyRef: null,
  department: '',
  email: '',
  name: '',
  phone: '',
  status: { label: 'Active', value: 'ACTIVE' },
};

export type AgencyContactFormRef = {
  submit: () => void;
  reset: () => void;
};

type AgencyContactFormProps = {
  formRef?: Ref<AgencyContactFormRef>;
  onSubmit?: (values: AgencyContactFormValues) => void;
  defaultValues?: AgencyContactFormValues;
};

const AgencyContactForm: React.FC<AgencyContactFormProps> = ({
  formRef,
  onSubmit,
  defaultValues: defaultValuesProps = {},
}) => {
  const { messages } = useTypedIntl();

  const [form] = Form.useForm<AgencyContactFormValues>();

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
    reset() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps<AgencyContactFormValues>['onFinish'] = (values) => {
    onSubmit?.(values);
  };

  const onFinishFailed: FormProps<AgencyContactFormValues>['onFinishFailed'] = (
    errorInfo,
  ) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 20 }}
      className="p-2"
      initialValues={{
        ...defaultValues,
        ...defaultValuesProps,
      }}
    >
      <Form.Item
        label={messages['common.name']}
        name="name"
        rules={[
          {
            required: true,
            message: messages['form.pleaseInput'],
          },
        ]}
      >
        <AppInput placeholder={messages['form.enterName']} />
      </Form.Item>
      <Form.Item
        label={messages['common.email']}
        name="email"
        rules={[
          {
            required: true,
            message: messages['form.pleaseInput'],
          },
          {
            type: 'email',
            message: messages['form.enterValidEmail'],
          },
        ]}
      >
        <AppInput
          placeholder={messages['form.enterEmail']}
          allowCapitalize={false}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.phone']}
        name="phone"
        rules={[
          {
            validator: (_, value) => {
              if (!value) return Promise.resolve();
              if (!validatePhone(value)) {
                return Promise.reject(messages['form.enterValidMobile']);
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <AppInput placeholder={messages['form.enter']} />
      </Form.Item>
      <Form.Item
        label={messages['common.status']}
        name="status"
        rules={[
          {
            required: true,
            message: messages['form.pleaseSelect'],
          },
        ]}
      >
        <AppSelect
          options={defaultStatusOptions}
          placeholder={messages['form.selectStatus']}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.payrollCompany']}
        name="companyRef"
        rules={[
          {
            required: true,
            message: messages['form.pleaseSelect'],
          },
        ]}
      >
        <AppSelectSync
          type="company"
          apiType="searchCompany"
          placeholder={messages['form.selectCompany']}
          style={{ width: '100%' }}
          onChangeValue={(value: any) => {
            form.setFieldValue('companyRef', value);
          }}
        />
      </Form.Item>
      <Form.Item label={messages['common.branch']} name="department">
        <AppInput placeholder={messages['form.enterBranch']} />
      </Form.Item>
    </AppFormContainer>
  );
};

export default AgencyContactForm;
